/**
 * Stage 1 of the parse pipeline: character folding (plan §3, architecture.md §4.2).
 *
 * @remarks
 * Folding produces the canonical lowercase form every later stage consumes:
 *
 *   1. Unicode NFKC normalization (full-width forms, ligatures, compatibility characters).
 *   2. Typographic folding — curly apostrophes → `'`; double quotes, dashes, ellipses → spaces.
 *   3. Case folding to lowercase.
 *   4. Diacritic folding (D226, E13) — `léo` and `leo` are one word, see {@link foldDiacritics}.
 *   5. Structural punctuation (`: " ( ) [ ] { } /`) → spaces. Apostrophes and hyphens survive:
 *      `don't` and `level-one` are single words to the parser.
 *   6. Whitespace collapse (tabs, NBSP, runs of spaces) + trim.
 *
 * **Clause separators (`. , ; ! ?`) are deliberately PRESERVED** — this is the
 * v1 contract revision (plan §3.1): command chaining needs the boundaries
 * (`take card, swipe panel`), so the splitter consumes them downstream
 * (`tokenize/split-on-separators`). The pre-v1 `normalizeCommandInput`, which
 * destroyed them, is superseded by this module.
 *
 * Pure and deterministic: same input ⇒ same output, byte for byte. Folding is
 * idempotent — folding already-folded text changes nothing.
 *
 * @packageDocumentation
 */

/** Typographic characters folded to a straight apostrophe (word-internal, preserved). */
const APOSTROPHE_VARIANTS_PATTERN = /[‘’‚‛]/gu;

/**
 * Typographic characters with no lexical value: folded to spaces.
 *
 * The ellipsis (`…`) is deliberately ABSENT: NFKC (step 1) already expands it
 * to three ASCII periods, which the separator-preserving contract then keeps —
 * so `wait…take cover` becomes `wait...take cover` and the clause splitter
 * reads it as two commands. That is truer to player intent than gluing the
 * words together; discovered by the M1 suite, kept on purpose.
 */
const SPACE_FOLDED_PATTERN = /[“”„‟–—]/gu;

/**
 * Structural punctuation with no lexical value to the parser, folded to
 * spaces. Clause separators (`[.,;!?]`) are deliberately absent — see the
 * module remarks. Apostrophes (`'`) and hyphens (`-`) are word-internal.
 */
const STRUCTURAL_PUNCTUATION_PATTERN = /[/:"()[\]{}]/gu;

/** Any whitespace run (spaces, tabs, NBSP via the `u`-flag `\s` class). */
const WHITESPACE_RUN_PATTERN = /\s+/gu;

/** Every combining mark — what is left of an accent once a letter is decomposed (NFD). */
const COMBINING_MARK_PATTERN = /\p{M}/gu;

/**
 * Strip the diacritics from text (D226, E13): `léo` → `leo`, `Marchant's
 * café` → `marchant's cafe` once lowercased.
 *
 * @remarks
 * A player at an English keyboard types `x leo` for the jar scratched LÉO,
 * and the pack's `léo` must meet it. Accents are decided on BOTH sides of
 * every comparison the parser makes — the player's input here, and every
 * authored word where it is normalized for matching (the catalog's names,
 * the lexicon's tokens, the fuzzy pool's candidates) — so neither side can
 * carry an accent the other dropped. The transcript is untouched: folding is
 * for matching only, and the jar still prints as LÉO.
 *
 * Canonical decomposition (NFD) separates a base letter from its marks, the
 * marks are removed, and the result is recomposed (NFC) so a letter that
 * never carried a mark comes back exactly as it went in. Letters that do
 * not decompose (`ø`, `ß`, `æ`) are left as they are: there is no honest
 * ASCII for them, and inventing one would be a second rule.
 *
 * @param text - Any text, player-typed or authored
 * @returns The text with its combining marks removed
 */
export const foldDiacritics = (text: string): string => text.normalize('NFD').replace(COMBINING_MARK_PATTERN, '').normalize('NFC');

/**
 * Fold one raw player input into its canonical lowercase form, preserving
 * clause separators for the splitter.
 *
 * @param rawInput - Exactly what the player typed
 * @returns The folded text (empty string for blank input)
 */
export const foldCharacters = (rawInput: string): string =>
	foldDiacritics(
		rawInput
			.normalize('NFKC')
			.replace(APOSTROPHE_VARIANTS_PATTERN, "'")
			.replace(SPACE_FOLDED_PATTERN, ' ')
			.toLowerCase(),
	)
		.replace(STRUCTURAL_PUNCTUATION_PATTERN, ' ')
		.replace(WHITESPACE_RUN_PATTERN, ' ')
		.trim();
